import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import { map, sortBy, get, noop } from 'lodash-es';
import { Container } from 'reactstrap';
import {
  EXPANDED_FILTERS,
  FACETS_CONFIG,
  FACETS_GROUPS_CONFIG,
  FACETS_SUBGROUPS_CONFIG,
  GROUPS_KEYS,
  SUBGROUP_KEYS,
} from 'client/site-modules/car-buying-online/constants/usurp-filters-config';
import { DISPLAY_PRICE, MODEL } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { GLOBAL_HEADER_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { SKIP_TO_LINKS_CONFIG } from 'site-modules/shared/constants/inventory/routes-constants';
import { UsurpFilteringByCollapse } from 'site-modules/shared/components/inventory/usurp-filtering-by/usurp-filtering-by-collapse';
import { FiltersGroupWrapper } from 'site-modules/shared/components/inventory/usurp-filters-group/filters-group-wrapper';
import { UsurpFiltersGroup } from 'site-modules/shared/components/inventory/usurp-filters-group/usurp-filters-group';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { UsurpTabsGroup } from 'site-modules/shared/components/inventory/usurp-tabs-group/usurp-tabs-group';
import { UsurpMultiSelectGroup } from 'site-modules/shared/components/inventory/usurp-multi-select-group/usurp-multi-select-group';
import { UsurpZipInput } from 'site-modules/shared/components/inventory/usurp-zip-input/usurp-zip-input';
import { SuggestedFacets } from 'site-modules/car-buying-online/components/suggested-facets/suggested-facets';
import { ActiveFiltersGroup } from 'site-modules/shared/components/inventory/active-filters-group/active-filters-group';
import { EvEBuyingExperienceButtonGroup } from 'site-modules/shared/components/eve-buying-experience-button-group/eve-buying-experience-button-group';
import { ONLINE_BTN } from 'site-modules/shared/constants/inventory/buying-experience';

import './usurp-filters.scss';

export const UsurpFilters = memo(
  ({
    facets = [],
    filteringByFacets = [],
    paymentType = null,
    isDrawer = false,
    selectedFacets = {},
    setIsMultiMM = noop,
    searchResultsFound,
    onZipCodeUpdate,
    onUpdate,
    isMobile = false,
    includeDeliveryFee = false,
    onToggleDeliveryFees = noop,
  }) => {
    const getFacetConfig = facet => {
      const { type, group: subgroupName } = facet;

      return subgroupName ? FACETS_SUBGROUPS_CONFIG[subgroupName] : FACETS_CONFIG[type];
    };

    const filterValues = (values, config) => {
      const { excludeZeroCount, itemsToShow, hideWhenAllValuesEmpty } = config;
      let updatedValues = itemsToShow ? values.filter(item => itemsToShow.includes(item.name)) : values;
      updatedValues = updatedValues.filter(
        value => !excludeZeroCount || (value && value.count !== 0) || value.selected
      );

      if (hideWhenAllValuesEmpty && updatedValues.every(item => item.count === 0 && !item.selected)) {
        return [];
      }

      return updatedValues;
    };

    const filterOutEmptyFacets = () =>
      facets.filter(facet => {
        const config = getFacetConfig(facet);

        if (!config) {
          return false;
        }

        const { hideWhenAllValuesEmpty } = config;

        if (hideWhenAllValuesEmpty) {
          return !!filterValues(facet.values, config).length;
        }

        return true;
      });

    const getFacets = data => {
      const subgroupFacets = [];

      data.forEach(facet => {
        const { type } = facet;
        const config = FACETS_CONFIG[type];

        if (config) {
          const { indexInsideGroup } = config;
          const facetProps = { config, indexInsideGroup, ...facet };

          subgroupFacets.push(facetProps);
        }
      });
      return sortBy(subgroupFacets, ['indexInsideGroup']);
    };

    const getGroupedFacets = () => {
      const groups = {
        [GROUPS_KEYS.LOCATION_GROUP]: [],
      };

      filterOutEmptyFacets().forEach(facet => {
        const { group: subgroupName, rangeFacet, values } = facet;
        const config = getFacetConfig(facet);
        const { group, indexInsideGroup } = config;
        const facetProps = subgroupName
          ? { group: subgroupName, config, indexInsideGroup, rangeFacet, values: getFacets(values) }
          : {
              config,
              indexInsideGroup,
              ...facet,
              values: facet.values && [...filterValues(facet.values, config)],
            };

        if (groups[group]) {
          groups[group].push(facetProps);
        } else {
          groups[group] = [facetProps];
        }
      });

      return map(groups, (groupFacets, groupKey) => {
        const groupConfig = get(FACETS_GROUPS_CONFIG, [groupKey], {});

        return {
          groupKey,
          facets: sortBy(groupFacets, ['indexInsideGroup']),
          ...groupConfig,
        };
      });
    };

    const getGroupComponent = ({ group }) => {
      const { groupKey, facets: groupedFacets, withInnerDividers } = group;

      const modelFacetValues = get(
        facets.find(({ type }) => type === MODEL),
        'groups',
        {}
      );
      const GeneralGroupComponent = (
        <UsurpFiltersGroup
          facets={!groupedFacets.length ? [] : groupedFacets.filter(facet => !facet.config.subgroup)}
          onUpdate={onUpdate}
          withInnerDividers={withInnerDividers}
          selectedFacets={selectedFacets}
          modelFacetValues={modelFacetValues}
          facetsConfig={FACETS_CONFIG}
        />
      );

      switch (groupKey) {
        case GROUPS_KEYS.LOCATION_GROUP: {
          return (
            <Fragment>
              <UsurpZipInput onZipCodeUpdate={onZipCodeUpdate} className="mb-1" />
              {GeneralGroupComponent}
            </Fragment>
          );
        }
        case GROUPS_KEYS.PRICE_PAYMENT_GROUP: {
          const selectedTab = paymentType || DISPLAY_PRICE;
          const pricePaymentFacets = groupedFacets.filter(
            facet => facet.config.subgroup === SUBGROUP_KEYS.PRICE_PAYMENT_TABS_SUBGROUP
          );
          return (
            <Fragment>
              <UsurpTabsGroup
                facets={pricePaymentFacets}
                selectedTab={selectedTab}
                onUpdate={onUpdate}
                key={selectedTab}
                isMobile={isDrawer}
                facetsConfig={FACETS_CONFIG}
                searchResultsFound={searchResultsFound}
              />
              {GeneralGroupComponent}
            </Fragment>
          );
        }
        case GROUPS_KEYS.MM_GROUP: {
          return (
            <UsurpMultiSelectGroup
              facets={groupedFacets.filter(facet => !facet.config.subgroup)}
              onUpdate={onUpdate}
              withInnerDividers={withInnerDividers}
              selectedFacets={selectedFacets}
              setIsMultiMM={setIsMultiMM}
              facetsConfig={FACETS_CONFIG}
            />
          );
        }
        default: {
          return GeneralGroupComponent;
        }
      }
    };

    const groupedFacets = getGroupedFacets();

    return (
      <Container
        className="usurp-filters text-gray-darker px-0"
        tabIndex={-1}
        role="region"
        aria-labelledby="filters-panel-header"
        id={SKIP_TO_LINKS_CONFIG.FILTERS_CONTAINER_ID}
      >
        <div className="visually-hidden" role="heading" aria-level={2} id="filters-panel-header">
          Filters
        </div>
        {!isDrawer && (
          <>
            <ScrollLink
              to={SKIP_TO_LINKS_CONFIG.RESULTS_CONTAINER_ID}
              className="visually-hidden-focusable d-none d-lg-block"
              scrollConfig={{ offset: GLOBAL_HEADER_OFFSET }}
            >
              {SKIP_TO_LINKS_CONFIG.RESULTS_CONTAINER_LABEL}
            </ScrollLink>
            {!!filteringByFacets.length && <ActiveFiltersGroup facets={filteringByFacets} onUpdate={onUpdate} />}
            <EvEBuyingExperienceButtonGroup
              selectedFacets={selectedFacets}
              isMobile={isMobile}
              selectedBtn={ONLINE_BTN}
              showDeliveryFreeToggle
              toggleDeliveryFees={onToggleDeliveryFees}
              includeDeliveryFee={includeDeliveryFee}
              headingTag="h3"
            />
          </>
        )}
        {isDrawer && (
          <UsurpFilteringByCollapse
            facets={filteringByFacets}
            onUpdate={onUpdate}
            className="py-1 px-1"
            suggestedFacetsComponent={SuggestedFacets}
          />
        )}
        {sortBy(groupedFacets, ['order']).map((group, index) => {
          const { groupKey, title, creativeId, isSrOnlyTitle, tooltip, adornmentGroupConfig, legendConfig } = group;
          const withCollapse = !EXPANDED_FILTERS.includes(groupKey);

          const GroupComponent = getGroupComponent({
            group,
          });
          return GroupComponent ? (
            <FiltersGroupWrapper
              key={groupKey}
              title={title}
              isSrOnlyTitle={isSrOnlyTitle}
              creativeId={creativeId}
              withCollapse={withCollapse}
              hideCollapseSeparator={index === groupedFacets.length - 1}
              tooltip={tooltip}
              adornmentGroupConfig={adornmentGroupConfig}
              legendConfig={legendConfig}
            >
              {GroupComponent}
            </FiltersGroupWrapper>
          ) : null;
        })}
      </Container>
    );
  }
);
UsurpFilters.propTypes = {
  onZipCodeUpdate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  facets: PropTypes.arrayOf(PropTypes.shape({})),
  filteringByFacets: PropTypes.arrayOf(PropTypes.shape({})),
  paymentType: PropTypes.string,
  isDrawer: PropTypes.bool,
  selectedFacets: PropTypes.shape({}),
  setIsMultiMM: PropTypes.func,
  searchResultsFound: PropTypes.number,
  isMobile: PropTypes.bool,
  includeDeliveryFee: PropTypes.bool,
  onToggleDeliveryFees: PropTypes.func,
};
